import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Alert, Button, Container, Link as MuiLink, Paper, TextField, Typography } from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../../hooks";
import { clientSetPassword } from "../../../store/thunks/clientAuthThunks";

const schema = z
  .object({
    password: z.string().min(6, "Mínimo 6 caracteres"),
    confirmPassword: z.string().min(6, "Mínimo 6 caracteres"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

const RecoverLink = () => (
  <Typography variant="body2" align="center">
    <MuiLink component={Link} to="/facturacion/recuperar-contrasena">Pedir un enlace nuevo</MuiLink>
  </Typography>
);

/** Where the emailed link lands: choose the password and enter the portal (GH126). */
export const CreatePasswordPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [error, setError] = useState<string>();

  const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onSubmit = async ({ password }: FormData) => {
    setError(undefined);
    try {
      await dispatch(clientSetPassword(token, password));
      navigate("/facturacion", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar la contraseña. Intenta de nuevo.");
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" align="center" gutterBottom fontWeight="bold" sx={{ color: "#0e368d" }}>
          Crea tu contraseña
        </Typography>

        {!token ? (
          <>
            <Alert severity="error" sx={{ my: 2 }}>
              El enlace está incompleto. Ábrelo tal cual desde el correo, o pide uno nuevo.
            </Alert>
            <RecoverLink />
          </>
        ) : (
          <>
            <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
              Portal de Facturación - Hospital Veterinario Peninsular
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Contraseña nueva"
                    type="password"
                    fullWidth
                    margin="normal"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    autoComplete="new-password"
                    autoFocus
                  />
                )}
              />
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirmar contraseña"
                    type="password"
                    fullWidth
                    margin="normal"
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    autoComplete="new-password"
                  />
                )}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                sx={{ mt: 2, mb: 2, backgroundColor: "#0e368d", "&:hover": { backgroundColor: "#0b2b71" } }}
              >
                {isSubmitting ? "Guardando..." : "Guardar y entrar"}
              </Button>
            </form>

            {error && <RecoverLink />}
          </>
        )}
      </Paper>
    </Container>
  );
};
